import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuth } from './useAuth';
import { SPECIAL_REPORTS_UPLOAD_CONFIG } from '../utils/constants';
import type { SupplierSpecialReport } from '../types';

const REPORTS_QUERY_KEY = 'supplier_special_reports';
const SIGNED_URL_EXPIRY_SEC = 60;

function sanitizeFileName(name: string): string {
  const base = name.replace(/^.*[/\\]/, '').trim();
  const safe = base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
  return safe || 'document';
}

function validateFile(file: File): string | null {
  const allowed = SPECIAL_REPORTS_UPLOAD_CONFIG.ALLOWED_TYPES as readonly string[];
  if (!allowed.includes(file.type)) {
    return 'Invalid file type. Allowed: PDF, Word (.doc/.docx), PowerPoint (.ppt/.pptx).';
  }
  if (file.size > SPECIAL_REPORTS_UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return `File too large. Maximum size is ${SPECIAL_REPORTS_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB.`;
  }
  return null;
}

/** All special reports visible to the current user (RLS filters by supplier visibility). */
export function useAllSpecialReports() {
  const query = useQuery<SupplierSpecialReport[]>({
    queryKey: [REPORTS_QUERY_KEY, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_special_reports')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SupplierSpecialReport[];
    },
    enabled: true,
    staleTime: 30000,
  });
  return {
    reports: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Reports for a single supplier. */
export function useSupplierSpecialReports(supplierId: string | null) {
  const query = useQuery<SupplierSpecialReport[]>({
    queryKey: [REPORTS_QUERY_KEY, supplierId],
    queryFn: async () => {
      if (!supplierId) return [];
      const { data, error } = await supabase
        .from('supplier_special_reports')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SupplierSpecialReport[];
    },
    enabled: !!supplierId,
    staleTime: 30000,
  });
  return {
    reports: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Get a signed download URL for a report file (valid for 60s). */
export async function getDownloadUrl(filePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(SPECIAL_REPORTS_UPLOAD_CONFIG.STORAGE_BUCKET)
    .createSignedUrl(filePath, SIGNED_URL_EXPIRY_SEC);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export function useSupplierSpecialReportsMutations() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async ({ supplierId, file }: { supplierId: string; file: File }) => {
      const err = validateFile(file);
      if (err) throw new Error(err);
      if (!isAdmin) throw new Error('Only admins can upload special reports.');

      const safeName = sanitizeFileName(file.name);
      const unique = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      const filePath = `${supplierId}/${unique}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from(SPECIAL_REPORTS_UPLOAD_CONFIG.STORAGE_BUCKET)
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;

      const { data: row, error: insertError } = await supabase
        .from('supplier_special_reports')
        .insert({
          supplier_id: supplierId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          content_type: file.type,
        })
        .select()
        .single();
      if (insertError) throw insertError;
      return row as SupplierSpecialReport;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REPORTS_QUERY_KEY] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (reportId: string) => {
      if (!isAdmin) throw new Error('Only admins can delete special reports.');

      const { data: row, error: fetchError } = await supabase
        .from('supplier_special_reports')
        .select('file_path')
        .eq('id', reportId)
        .single();
      if (fetchError || !row) throw new Error('Report not found.');

      const { error: removeError } = await supabase.storage
        .from(SPECIAL_REPORTS_UPLOAD_CONFIG.STORAGE_BUCKET)
        .remove([(row as { file_path: string }).file_path]);
      if (removeError) throw removeError;

      const { error: deleteError } = await supabase
        .from('supplier_special_reports')
        .delete()
        .eq('id', reportId);
      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REPORTS_QUERY_KEY] });
    },
  });

  return {
    uploadReport: uploadMutation.mutateAsync,
    deleteReport: deleteMutation.mutateAsync,
    uploading: uploadMutation.isPending,
    deleting: deleteMutation.isPending,
    uploadError: uploadMutation.error,
    deleteError: deleteMutation.error,
  };
}
